import { indentLess, indentMore } from "@codemirror/commands";
import { jsonParseLinter } from "@codemirror/lang-json";
import { forceLinting, linter, setDiagnostics } from "@codemirror/lint";
import { EditorView, type ViewUpdate } from "@codemirror/view";
import { designVariables } from "@studio3t/design-variables";
import {
  ButtonSmallSubtleIcon,
  GenericDropdown,
  type GenericDropdownItem,
  Icon,
  IconSVG,
  Tooltip,
} from "@studio3t/generic-components";
import CodeMirror, { type ReactCodeMirrorRef } from "@uiw/react-codemirror";
import {
  type Dispatch,
  type ReactNode,
  type RefObject,
  type SetStateAction,
  useMemo,
  useRef,
  useState,
} from "react";
import styled, { type DefaultTheme, useTheme } from "styled-components";
import {
  buildExtensions,
  CONTEXT_MENU_HEIGHT,
  editorRegionFocusRing,
} from "@/components/JsonViewer";
import { OverlayScroll } from "@/components/OverlayScroll";
import { ErrorStatusButton, jumpToOffset } from "./EditorErrorsPopover";
import { type EditorErrorRow, jsonErrorRows } from "./editorErrors";
import { singleLinePlaceholder } from "./editorPlaceholder";
import { useStudioPipeline } from "./StudioPipelineProvider";
import { inputEmptied } from "./stagingModel";
import { useEditorFocusRequest } from "./useEditorFocusRequest";
import { viewOnlyExtensions } from "./viewOnlyEditor";

// Figma StatusBar component (UI-Modules 1660-55289): Editor/statusbar tokens —
// 5px padding both axes, 4px gap, 28px Button/S subtle icon buttons, and an
// 8px-wide divider slot holding a centered 1x20px line.
const STATUS_PADDING = 5;
const STATUS_GAP = 4;

// Metrics shared with the JsonViewer template: 24px code lines behind a
// 33px line-number gutter + 14px fold gutter + 20px lint column (reserved
// in every editor so the sidebar width never varies); content starts
// after the cm-content 5px top padding. The empty-state links sit on
// lines 2 and 3.
export const LINE_HEIGHT = 24;
export const GUTTER_WIDTH = 33 + 14 + 20;
export const CONTENT_PAD_TOP = 5;

// Same platform-shortcut badges as the JsonViewer context menu.
const IS_MAC =
  typeof navigator !== "undefined" &&
  (navigator.platform ?? "").toUpperCase().includes("MAC");
const KEY = (letter: string) => (IS_MAC ? `⌘${letter}` : `Ctrl+${letter}`);

export interface EditorSample {
  id: string;
  label: string;
  content: string;
}

interface CursorPosition {
  line: number;
  col: number;
}

// A full-document replacement (loading another schema or sample into the
// editor, a workspace switch) keeps the previous document's diagnostics
// remapped onto the new text until the linter's idle delay expires — a clean
// document flashes covered in stale squiggles. Clear them on the swap and
// lint the fresh document right away. Growing an *empty* document is
// exempt, so typing the first character keeps the normal lint delay.
// Exported for the pipeline code editor, which swaps documents the same way.
export const relintOnDocSwap = EditorView.updateListener.of((u) => {
  if (!u.docChanged) return;
  const oldLength = u.startState.doc.length;
  if (oldLength === 0) return;
  let wholeDocReplaced = false;
  u.changes.iterChangedRanges((fromA, toA) => {
    if (fromA === 0 && toA === oldLength) wholeDocReplaced = true;
  });
  if (!wholeDocReplaced) return;
  // Dispatching inside an update is not allowed — clear on the next tick.
  queueMicrotask(() => {
    u.view.dispatch(setDiagnostics(u.view.state, []));
    forceLinting(u.view);
  });
});

// Copies the main selection to the clipboard and returns it; null when the
// selection is empty. Clipboard write can be denied — a failure is a no-op.
function writeSelectionToClipboard(view: EditorView) {
  const sel = view.state.selection.main;
  if (sel.empty) return null;
  navigator.clipboard
    .writeText(view.state.sliceDoc(sel.from, sel.to))
    .catch(() => {});
  return sel;
}

// The editor-wide commands the status bar and context menu share: JSON
// formatting, CodeMirror indent commands, and ref-based clipboard actions.
// Exported for the pipeline code editor, which shares the menu but owns its
// chrome.
export function useEditorCommands(
  cmRef: RefObject<ReactCodeMirrorRef | null>,
  value: string,
  onChange: (value: string) => void,
) {
  const formatDocument = () => {
    try {
      onChange(JSON.stringify(JSON.parse(value), null, 2));
    } catch {
      // Not valid JSON (yet) — formatting is a no-op, never an error.
    }
  };

  const runCommand = (command: typeof indentMore) => {
    const view = cmRef.current?.view;
    if (!view) return;
    command(view);
    view.focus();
  };

  const copySelection = () => {
    const view = cmRef.current?.view;
    if (view) writeSelectionToClipboard(view);
  };

  const cutSelection = () => {
    const view = cmRef.current?.view;
    if (!view) return;
    const sel = writeSelectionToClipboard(view);
    if (!sel) return;
    view.dispatch({ changes: { from: sel.from, to: sel.to } });
    view.focus();
  };

  const pasteFromClipboard = async () => {
    const view = cmRef.current?.view;
    if (!view) return;
    // Clipboard read needs user permission; a denial is just a no-op.
    const text = await navigator.clipboard.readText().catch(() => "");
    if (!text) return;
    view.dispatch(view.state.replaceSelection(text));
    view.focus();
  };

  return {
    formatDocument,
    runCommand,
    copySelection,
    cutSelection,
    pasteFromClipboard,
  };
}

interface JsonEditorExtensionsOptions {
  theme: DefaultTheme;
  wrap: boolean;
  setCursor: Dispatch<SetStateAction<CursorPosition>>;
  setHasSelection: Dispatch<SetStateAction<boolean>>;
  locked: boolean;
}

// CodeMirror extension set: the shared viewer chrome, JSON lint squiggles +
// gutter dots, the placeholder tint, and the status-readout listener.
function useJsonEditorExtensions(options: JsonEditorExtensionsOptions) {
  const { theme, wrap, setCursor, setHasSelection, locked } = options;
  return useMemo(
    () => [
      ...buildExtensions(theme, { lineWrapping: wrap }),
      ...viewOnlyExtensions(locked),
      // JSON syntax errors get the red wavy underline (same squiggles as
      // the pipeline code editor); an empty document is never flagged.
      // jsonParseLinter emits zero-width ranges (rendered as a bare point
      // marker) — stretch those over the errored line so it squiggles.
      linter((view) => {
        if (view.state.doc.length === 0) return [];
        return jsonParseLinter()(view).map((d) => {
          if (d.from !== d.to) return d;
          const line = view.state.doc.lineAt(
            Math.min(d.from, view.state.doc.length),
          );
          return { ...d, from: line.from, to: line.to };
        });
      }),
      relintOnDocSwap,
      singleLinePlaceholder(theme.editorLineNumTxt),
      // Cursor position for the status bar readout + selection presence for
      // the context menu's Cut/Copy.
      EditorView.updateListener.of((u) => {
        if (!u.selectionSet && !u.docChanged) return;
        const main = u.state.selection.main;
        const line = u.state.doc.lineAt(main.head);
        setCursor({ line: line.number, col: main.head - line.from + 1 });
        setHasSelection(!main.empty);
      }),
    ],
    [theme, wrap, setCursor, setHasSelection, locked],
  );
}

// The context-menu model — same shape as the old Monaco input editor's menu
// (samples included, per useInputEditorActions). The handlers close over the
// live editor ref, so the items are rebuilt per render by the caller.
// Exported for the pipeline code editor (samples empty there — the old
// /studio pipeline editor had no special menu items either).
/** The view-only studio's context menu (editsLocked): copying and nothing
 *  else — the regular menu's Copy entry (selection-gated, same shortcut)
 *  plus "Copy all", the whole document to the clipboard. Every
 *  document-changing entry, and the wrap toggle (wrap is always on there),
 *  stays out. */
export function viewOnlyContextItems(
  items: GenericDropdownItem[],
  locked: boolean,
  documentText: string,
): GenericDropdownItem[] {
  if (!locked) return items;
  const copy = items
    .flat()
    .find((item) => item !== "SEPARATOR" && item.id === "copy");
  const copyAll: GenericDropdownItem[number] = {
    id: "copy-all",
    type: "text",
    value: "copy-all",
    label: "Copy all",
    disabled: documentText === "",
    closeOnSelect: true,
    // Clipboard write can be denied — a failure is a no-op.
    onClick: () => {
      navigator.clipboard.writeText(documentText).catch(() => {});
    },
  };
  return [copy ? [copy, copyAll] : [copyAll]];
}

export function buildContextItems(options: {
  empty: boolean;
  wrap: boolean;
  hasSelection: boolean;
  samples: EditorSample[];
  onChange: (value: string) => void;
  commands: ReturnType<typeof useEditorCommands>;
  onToggleWrap: () => void;
}): GenericDropdownItem[] {
  const { empty, wrap, hasSelection, samples, onChange, commands } = options;
  const sampleEntries =
    samples.length > 0
      ? ([
          "SEPARATOR",
          ...samples.map((sample) => ({
            id: sample.id,
            type: "text" as const,
            value: sample.id,
            label: sample.label,
            closeOnSelect: true,
            onClick: () => onChange(sample.content),
          })),
        ] as GenericDropdownItem[number][])
      : [];

  return [
    [
      {
        id: "cut",
        type: "text",
        value: "cut",
        label: "Cut",
        badgeText: KEY("X"),
        disabled: !hasSelection,
        closeOnSelect: true,
        onClick: commands.cutSelection,
      },
      {
        id: "copy",
        type: "text",
        value: "copy",
        label: "Copy",
        badgeText: KEY("C"),
        disabled: !hasSelection,
        closeOnSelect: true,
        onClick: commands.copySelection,
      },
      {
        id: "paste",
        type: "text",
        value: "paste",
        label: "Paste",
        badgeText: KEY("V"),
        closeOnSelect: true,
        onClick: commands.pasteFromClipboard,
      },
      ...sampleEntries,
      "SEPARATOR",
      {
        id: "format-json",
        type: "text",
        value: "format-json",
        label: "Format JSON",
        disabled: empty,
        closeOnSelect: true,
        onClick: commands.formatDocument,
      },
      {
        id: "indent-less",
        type: "text",
        value: "indent-less",
        label: "Decrease indent",
        disabled: empty,
        closeOnSelect: true,
        onClick: () => commands.runCommand(indentLess),
      },
      {
        id: "indent-more",
        type: "text",
        value: "indent-more",
        label: "Increase indent",
        disabled: empty,
        closeOnSelect: true,
        onClick: () => commands.runCommand(indentMore),
      },
      {
        id: "word-wrap",
        type: "text",
        value: "word-wrap",
        label: wrap ? "Disable word wrap" : "Enable word wrap",
        disabled: empty,
        closeOnSelect: true,
        onClick: options.onToggleWrap,
      },
    ],
  ];
}

// The zero-point state's Link-styled sample loaders under the
// placeholder line (Figma 14397-167447).
function EmptyStateSamples({
  samples,
  onChange,
}: {
  samples: EditorSample[];
  onChange: (value: string) => void;
}) {
  const theme = useTheme();
  return (
    <EmptyStateOverlay>
      {samples.map((sample) => (
        <SampleLink
          key={sample.id}
          type="button"
          onClick={() => onChange(sample.content)}
        >
          <IconSVG
            icon={Icon.Right}
            size={16}
            colors={{ default: theme.linkColor }}
          />
          {sample.label}
        </SampleLink>
      ))}
    </EmptyStateOverlay>
  );
}

// Figma Editor/StatusBar: format / outdent / indent / wrap, then the clear
// control behind a divider. Everything is disabled in the zero-point (empty)
// state — the Figma mock shows the subtle-disabled icon gray. GenericButton
// derivatives drop the title prop, so the tooltips wrap the buttons (shared
// Tooltip, as in App's top nav).
interface ErrorsSlotProps {
  rows: EditorErrorRow[];
  documentName: string;
  onJump: (from: number) => void;
}

function ValidJsonIcon({ testIdBase }: { testIdBase: string }) {
  const theme = useTheme();
  return (
    <StatusIconSlot title="Valid JSON" data-testid={`${testIdBase}-validity`}>
      <IconSVG
        icon={Icon.Confirm}
        size={16}
        colors={{ default: theme.semanticMainPrimary }}
      />
    </StatusIconSlot>
  );
}

function EditorStatusBar({
  empty,
  wrap,
  onToggleWrap,
  onClear,
  commands,
  statusExtras,
  cursor,
  valid,
  errors,
  testIdBase,
}: {
  empty: boolean;
  wrap: boolean;
  onToggleWrap: () => void;
  onClear: () => void;
  commands: ReturnType<typeof useEditorCommands>;
  statusExtras?: ReactNode;
  cursor: CursorPosition;
  valid: boolean | null;
  errors: ErrorsSlotProps;
  testIdBase: string;
}) {
  return (
    <StatusBar>
      <StatusActions>
        <Tooltip text="Format JSON" side="top">
          <ButtonSmallSubtleIcon
            icon={Icon.FormatQuery}
            onClick={commands.formatDocument}
            disabled={empty}
          />
        </Tooltip>
        <Tooltip text="Decrease indent" side="top">
          <ButtonSmallSubtleIcon
            icon={Icon.IndentLeft}
            onClick={() => commands.runCommand(indentLess)}
            disabled={empty}
          />
        </Tooltip>
        <Tooltip text="Increase indent" side="top">
          <ButtonSmallSubtleIcon
            icon={Icon.IndentRight}
            onClick={() => commands.runCommand(indentMore)}
            disabled={empty}
          />
        </Tooltip>
        <Tooltip
          text={wrap ? "Disable word wrap" : "Enable word wrap"}
          side="top"
        >
          <ButtonSmallSubtleIcon
            icon={Icon.WordWrap}
            onClick={onToggleWrap}
            disabled={empty}
          />
        </Tooltip>
        <StatusDivider />
        <Tooltip text="Clear" side="top">
          <ButtonSmallSubtleIcon
            icon={Icon.Clean}
            onClick={onClear}
            disabled={empty}
          />
        </Tooltip>
        {statusExtras && (
          <>
            <StatusDivider />
            {statusExtras}
          </>
        )}
      </StatusActions>
      {/* StatusBar/Status (Figma 1660-55304): cursor readout + validity
          icon; hidden in the zero-point state. */}
      {valid !== null && (
        <StatusRight data-testid={`${testIdBase}-status`}>
          <CursorReadout>
            line {cursor.line}, pos {cursor.col}
          </CursorReadout>
          {valid ? (
            <ValidJsonIcon testIdBase={testIdBase} />
          ) : (
            <ErrorStatusButton
              rows={errors.rows}
              title="Invalid JSON"
              documentName={errors.documentName}
              onJump={errors.onJump}
              testIdBase={testIdBase}
            />
          )}
        </StatusRight>
      )}
    </StatusBar>
  );
}

// The zero-point state's validity: null while empty (the status block
// hides), else whether the parse produced no error rows.
function jsonValidity(empty: boolean, rows: EditorErrorRow[]): boolean | null {
  return empty ? null : rows.length === 0;
}

// The empty state's sample loaders show only while there is something to
// load and the studio allows typing.
function showsEmptySamples(
  empty: boolean,
  editsLocked: boolean,
  samples: EditorSample[],
): boolean {
  return empty && !editsLocked && samples.length > 0;
}

// An UNDO transaction just landed the document on the empty placeholder
// (the onUndoToEmpty gesture) — typed deletions, the Clean button and
// sample loads reach the editor as other user events and never match.
function undoReachedEmpty(next: string, update: ViewUpdate): boolean {
  if (next !== "") return false;
  return update.transactions.some((tr) => tr.isUserEvent("undo"));
}

// The two ways a user edit reaches the document — CodeMirror's change
// listener and the footer's Clean button — each passing the text on and
// telling the caller when the edit landed on the empty placeholder.
// CodeMirror's listener only hears user edits: @uiw/react-codemirror
// tags a controlled `value` change as external and keeps it from onChange,
// so opening a pipeline or loading a sample never gets here.
function useEmptyingEdits({
  value,
  onChange,
  onUndoToEmpty,
  onEmptied,
}: {
  value: string;
  onChange: (value: string) => void;
  onUndoToEmpty?: () => void;
  onEmptied?: () => void;
}) {
  const notifyEmptied = (previous: string, next: string) => {
    if (inputEmptied(previous, next)) onEmptied?.();
  };
  return {
    onEdit: (next: string, update: ViewUpdate) => {
      onChange(next);
      if (undoReachedEmpty(next, update)) onUndoToEmpty?.();
      notifyEmptied(update.startState.doc.toString(), next);
    },
    onClear: () => {
      onChange("");
      notifyEmptied(value, "");
    },
  };
}

// The Studio JSON editor panel shared by the input document and schema
// editors (Figma Bridge_PipelineCode 13970-93956 / 14442-316244): CodeMirror
// configured like the Raw configuration modal's viewer (shared
// buildExtensions), editable, with the design's zero-point empty state and
// the Editor/StatusBar strip at the bottom. `samples`, when given, render as
// Link-styled loaders on the empty state's lines 2+ and as context-menu
// entries (the input editor's sample documents).
export function JsonDocumentEditor({
  value,
  onChange,
  onUndoToEmpty,
  onEmptied,
  placeholder,
  testIdBase,
  documentName,
  samples = [],
  statusExtras,
  notice,
}: {
  value: string;
  onChange: (value: string) => void;
  /** An UNDO (⌘Z) landed the document on the empty placeholder — the
   *  gesture stepped back past the content fill, so the caller may take
   *  back the staging behind it too (TTTL-191: input editor unstages the
   *  source, schema editor removes the staged schema). Typed deletions,
   *  the Clean button and sample loads never fire this. */
  onUndoToEmpty?: () => void;
  /** The document reached the empty placeholder by any user edit — typed
   *  or cut away, the Clean button, or an undo. Programmatic value changes
   *  (opening a pipeline, loading a sample) never fire it. */
  onEmptied?: () => void;
  placeholder: string;
  /** How the Errors popover's "Fix with AI" names the document to the
   *  helper ("input document", "schema"). */
  documentName: string;
  /** data-testid prefix: `${base}-editor`, `${base}-status`, `${base}-validity`. */
  testIdBase: string;
  samples?: EditorSample[];
  /** Extra controls appended to the status bar's action group, behind a
   *  divider (e.g. the schema editor's encoding convert button). */
  statusExtras?: ReactNode;
  /** Overlay pinned to the bottom of the editor surface (e.g. the schema
   *  editor's not-referenced warning). Rendered inside the relatively
   *  positioned editor area so it never shifts the layout. */
  notice?: ReactNode;
}) {
  const theme = useTheme();
  const cmRef = useRef<ReactCodeMirrorRef>(null);
  useEditorFocusRequest(cmRef);
  const [wrapPref, setWrap] = useState(true);
  // The view-only studio always wraps — with nothing to edit, the toggle
  // (and the footer it lives in) has no job.
  const { editsLocked } = useStudioPipeline();
  const wrap = editsLocked || wrapPref;
  const [cursor, setCursor] = useState<CursorPosition>({ line: 1, col: 1 });
  const [hasSelection, setHasSelection] = useState(false);
  const editorAreaRef = useRef<HTMLDivElement>(null);
  const empty = value === "";

  // Right side of the status bar: JSON validity and the Errors popover's
  // rows, from one synchronous parse — the count is right on the frame
  // the state turns red. null while empty — the whole status block is
  // hidden in the zero-point state (Figma mock).
  const rows = useMemo(() => jsonErrorRows(value), [value]);
  const valid = jsonValidity(empty, rows);

  const extensions = useJsonEditorExtensions({
    theme,
    wrap,
    setCursor,
    setHasSelection,
    locked: editsLocked,
  });
  const commands = useEditorCommands(cmRef, value, onChange);
  const edits = useEmptyingEdits({ value, onChange, onUndoToEmpty, onEmptied });
  const onJump = (from: number) => jumpToOffset(cmRef.current?.view, from);
  const onToggleWrap = () => setWrap((prev) => !prev);

  // Rebuilt every render — the handlers close over the live editor ref.
  const contextItems = viewOnlyContextItems(
    buildContextItems({
      empty,
      wrap,
      hasSelection,
      samples,
      onChange,
      commands,
      onToggleWrap,
    }),
    editsLocked,
    value,
  );

  return (
    <Panel data-testid={`${testIdBase}-editor`}>
      <EditorArea ref={editorAreaRef}>
        {notice}
        <EditorScroll>
          <CodeMirror
            ref={cmRef}
            value={value}
            onChange={edits.onEdit}
            extensions={extensions}
            placeholder={placeholder}
            // In the zero-point state Tab leaves the editor for the sample
            // links below it — there is nothing to indent. With content, Tab
            // indents as usual; Escape then Tab still leaves (CodeMirror's
            // own escape hatch).
            indentWithTab={!empty}
            basicSetup={{
              lineNumbers: true,
              foldGutter: false,
              bracketMatching: true,
              closeBrackets: true,
              autocompletion: false,
              // Figma shows plain rows — no active-line wash (same as the
              // Raw configuration viewer).
              highlightActiveLine: false,
              highlightActiveLineGutter: false,
            }}
          />
          {showsEmptySamples(empty, editsLocked, samples) && (
            <EmptyStateSamples samples={samples} onChange={onChange} />
          )}
        </EditorScroll>
        <GenericDropdown
          asContextMenu={editorAreaRef}
          dropdownItems={contextItems}
          width={{ strategy: "fixed", size: 220 }}
          height={CONTEXT_MENU_HEIGHT}
        />
      </EditorArea>

      {/* The view-only studio has nothing to do in the footer — only
          copying is possible — so it goes. */}
      {!editsLocked && (
        <EditorStatusBar
          empty={empty}
          wrap={wrap}
          onToggleWrap={onToggleWrap}
          onClear={edits.onClear}
          commands={commands}
          statusExtras={statusExtras}
          cursor={cursor}
          valid={valid}
          errors={{ rows, documentName, onJump }}
          testIdBase={testIdBase}
        />
      )}
    </Panel>
  );
}

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: ${(p) => p.theme.editorBackground};
`;

const EditorArea = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
`;

// Same scrolling recipe as the Raw configuration modal: the editor grows to
// its content and OverlayScrollbars owns the overflow, so only this region
// scrolls — never the status bar. The inner chain stretches CodeMirror to at
// least the viewport height so clicks below a short document land in the
// editor.
const EditorScroll = styled(OverlayScroll)`
  flex: 1;
  min-height: 0;
  ${editorRegionFocusRing}

  /* Positioned so the zero-point overlay is placed against the scrolled
     content: it scrolls with the document and is clipped by the region,
     instead of hanging out over the status bar and whatever sits below
     when the region is short. */
  [data-overlayscrollbars-contents] {
    position: relative;
    display: flex;
    flex-direction: column;
    min-height: 100%;
  }

  [data-overlayscrollbars-contents] > div {
    display: flex;
    flex-direction: column;
    flex: 1;
  }

  .cm-editor {
    flex: 1;
  }
`;

// Links live where lines 2 and 3 would render (Figma zero-point state) —
// 24px rows flush with the code column, inside the scrolled content so they
// move and clip with the editor. The wrapper ignores pointer events so the
// rest of the surface still focuses the editor.
// Figma 14397-167447: the links block hangs 10px under the placeholder
// line, 16px links on 10px gaps, flush with the code column.
const EmptyStateOverlay = styled.div`
  position: absolute;
  top: ${CONTENT_PAD_TOP + LINE_HEIGHT + designVariables.ui.semantic.spacings}px;
  left: ${GUTTER_WIDTH + 4}px;
  display: flex;
  flex-direction: column;
  gap: ${designVariables.ui.semantic.spacings}px;
  pointer-events: none;
`;

// Shared with the Tests tab's placeholder links — same face and hover.
// The Figma Link component is 16px tall (14397-167447 / 14790-130431);
// the surrounding blocks add their own 10px gaps.
export const SampleLink = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${designVariables.ui.link.gap}px;
  height: 16px;
  padding: 0;
  border: none;
  background: none;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.2;
  color: ${(p) => p.theme.linkColor};
  cursor: pointer;
  pointer-events: auto;

  &:hover {
    text-decoration: underline;
  }
`;

const StatusBar = styled.div`
  display: flex;
  align-items: center;
  gap: ${STATUS_GAP}px;
  flex-shrink: 0;
  padding: ${STATUS_PADDING}px;
  background: ${(p) => p.theme.editorStatusBackground};
  border-top: 1px solid ${(p) => p.theme.editorStatusBorder};
`;

// Left half of the bar — the editing controls; pushes the status block right.
const StatusActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${STATUS_GAP}px;
  flex: 1;
  min-width: 0;
`;

const StatusRight = styled.div`
  display: flex;
  align-items: center;
  gap: ${STATUS_GAP}px;
  flex-shrink: 0;
`;

// Figma StatusBar/Status: Single-line/Small/Regular in semantic/textlight.
const CursorReadout = styled.span`
  font-size: 12px;
  font-weight: 400;
  line-height: 1.2;
  color: ${(p) => p.theme.semanticTextLight};
  white-space: nowrap;
`;

// Same 28px footprint as the Button/S controls, but purely informative.
const StatusIconSlot = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
`;

// Exported for status-bar extras that carry their own divider (the schema
// editor's converter | variants pair).
export const StatusDivider = styled.span`
  width: 8px;
  height: 20px;
  flex-shrink: 0;
  background-image: linear-gradient(
    ${(p) => p.theme.editorStatusBorder},
    ${(p) => p.theme.editorStatusBorder}
  );
  background-size: 1px 100%;
  background-position: center;
  background-repeat: no-repeat;
`;
